import styled, { css } from 'styled-components'
import { BaseCell } from '../shared/BaseCell'
import { HStack } from '@musinsa/mwms-ds'
import { colors, colorSystem } from '@/styles/theme'
import Text from '@/components/content/Text'
import { Switch } from '@/components/content/Switch/Switch'
import { CutOffSchedule, CutOffScheduleType, CutType } from '@/services/outboundSchedule/types'
import { useCutOffHoverContext } from '../../context/CutOffHoverContext'
import { useIsIn15MinCutOff } from '../../hooks/useIsIn15MinCutOff'
import { TooltipContent } from '@/components/content/Tooltip'
import { useUpdateCutOffScheduleStatus } from '@/services/outboundSchedule/useOutboundScheduleQueries'
import { Utils } from '@/utils/Utils'
import { getShipperScopeErrorMessage } from '@/utils/shipperScope'
import { AxiosError } from 'axios'
import MAlert from '@/utils/modal/MAlert'
import MToast from '@/utils/modal/MToast'
import { useIsExpiredCutOff } from '../../hooks/useIsExpiredCutOff'
import { useCutOffDateTimeContext } from '../../context/CutOffRowDateTimeContext'
import { useCutOffDate } from '../../hooks/useCutOffDate'

type Props = {
  cutType: CutType
  data: CutOffSchedule
}

// MEMO: 상시 컷오프만 사용 (임시는 스펙아웃)
const CutOffScheduleTypeMap: Record<CutOffScheduleType, string> = {
  TEMPORARY: '임시 컷오프',
  ONGOING: '상시 컷오프',
}

export function CutOffCell({ cutType, data }: Props) {
  const { scheduleType, priority, isActive, warehouseCode } = data

  const { date, time } = useCutOffDateTimeContext()
  const { isCutOffHover } = useCutOffHoverContext()

  const { shipperCode } = useCutOffDate()
  const isExpired = useIsExpiredCutOff({ date, time })
  const isIn15MinCutOff = useIsIn15MinCutOff({ date, time })

  const backgroundColor = isCutOffHover ? '#f4faff' : isExpired || !isActive ? '#00000008' : 'transparent'
  const textColor = isExpired ? colorSystem.text.common.tertiary : colorSystem.text.common.primary

  const scheduleLabel = CutOffScheduleTypeMap[scheduleType]
  const priorityText = priority === 0 || priority === undefined ? '합' : priority

  const updateCutOffScheduleStatus = useUpdateCutOffScheduleStatus()

  const handleActiveChange = async (isActive: boolean) => {
    try {
      await updateCutOffScheduleStatus.mutateAsync({
        date,
        time,
        warehouseCode,
        cutType,
        isActive,
        shipperCode,
      })
      MToast.show(`컷오프가 ${isActive ? 'ON' : 'OFF'} 되었습니다.`)
    } catch (e) {
      MAlert.show(getShipperScopeErrorMessage(e) ?? Utils.getApiErrMessage(e as AxiosError<ApiResponseV2>, '컷오프 ON/OFF 상태 변경에 실패했습니다.'))
    }
  }

  return (
    <Wrap $backgroundColor={backgroundColor} $isExpired={isExpired}>
      <HStack gap={4} align="center">
        <Tag>
          <Text kind="Body_12_medium" color={textColor}>
            {priorityText}
          </Text>
        </Tag>
        <Text kind="Body_12_regular" color={textColor}>
          {scheduleLabel}
        </Text>
      </HStack>

      {!isExpired && (
        <>
          <SwitchWrap className="switch-wrap">
            <Switch size="sm" disabled={isIn15MinCutOff} checked={isActive} onCheckedChange={handleActiveChange} />
            {isIn15MinCutOff && (
              <Tooltip className="tooltip" $isVisible $type="bottom" $width="144px">
                <Text kind="Body_12_medium" color={colorSystem.text.common.inverse}>
                  컷오프 15분 전부터는
                  <br />
                  모든 변경이 제한됩니다.
                </Text>
              </Tooltip>
            )}
          </SwitchWrap>
        </>
      )}
    </Wrap>
  )
}

const Wrap = styled(BaseCell)<{ $isExpired: boolean; $backgroundColor: string }>`
  flex: 1;
  justify-content: space-between;
  align-items: center;
  position: relative;
  background-color: ${({ $backgroundColor }) => $backgroundColor};

  ${({ $isExpired }) =>
    !$isExpired &&
    css`
      &:hover {
        outline: 1px solid ${colors.blue500};
        outline-offset: -1px;
        background-color: #f4faff;
      }
    `}
`

const SwitchWrap = styled.div`
  .tooltip {
    display: none;
  }

  &:hover {
    .tooltip {
      display: block;
    }
  }
`

const Tooltip = styled(TooltipContent)`
  position: absolute;
  right: -50px;
  text-align: center;

  &::before {
    right: 50%;
    transform: translateX(50%);
  }
`

const Tag = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background-color: ${colors.gray100};

  display: flex;
  justify-content: center;
  align-items: center;
`
